import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Download, FileSpreadsheet, FileText, ExternalLink, Send, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { MonthRate } from '../components/shared';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from 'recharts';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation, Trans } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function Reports() {
  const { t } = useTranslation('reports');
  const { user: currentUser } = useAuth();
  const isEmployee = currentUser?.role === 'employee';

  const [loading, setLoading] = useState(true);
  const [incomeByProject, setIncomeByProject] = useState([]);
  const [expensesByType, setExpensesByType] = useState([]);
  const [expensesByCategory, setExpensesByCategory] = useState({ by_category: [], by_project: [] });
  const [employeeEarnings, setEmployeeEarnings] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [currencyFilter, setCurrencyFilter] = useState('combined');
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  // The selected month's rate (set by <MonthRate>): its USD figures are at that rate
  const [exchangeRate, setExchangeRate] = useState(null);

  // Employee personal salary data (from /api/my/salary)
  const [mySalary, setMySalary] = useState(null);

  // Employee monthly trend (fetched separately, only when year changes)
  const [myTrend, setMyTrend] = useState([]);
  const [trendYear, setTrendYear] = useState(null);

  // Invoice / Telegram states
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceEmployees, setInvoiceEmployees] = useState([]);
  const [selectedInvoiceEmpIds, setSelectedInvoiceEmpIds] = useState([]);
  const [sendingInvoices, setSendingInvoices] = useState(false);
  const [invoiceResults, setInvoiceResults] = useState(null);
  const [loadingInvoiceEmps, setLoadingInvoiceEmps] = useState(false);
  const [showIncome, setShowIncome] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [selectedMonth, selectedYear, currencyFilter]);

  // Fetch trend only when year changes (not on every month change)
  useEffect(() => {
    if (!isEmployee || trendYear === selectedYear) return;
    const fetchTrend = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/my/salary/trend?year=${selectedYear}`, { withCredentials: true });
        setMyTrend(res.data || []);
        setTrendYear(selectedYear);
      } catch (e) {
        console.error('Error fetching trend:', e);
      }
    };
    fetchTrend();
  }, [isEmployee, selectedYear, trendYear]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const currencyParam = currencyFilter !== 'combined' ? `&currency_filter=${currencyFilter}` : '';

      if (isEmployee) {
        // Employee sees their own salary breakdown via /my/salary
        const salaryRes = await axios.get(`${API_URL}/api/my/salary?month=${selectedMonth}&year=${selectedYear}`, { withCredentials: true });
        setMySalary(salaryRes.data);
      } else {
        const [incomeRes, expensesRes, categoryRes, earningsRes] = await Promise.all([
          axios.get(`${API_URL}/api/reports/income-by-project?month=${selectedMonth}&year=${selectedYear}${currencyParam}`, { withCredentials: true }),
          axios.get(`${API_URL}/api/reports/expenses-by-type?month=${selectedMonth}&year=${selectedYear}${currencyParam}`, { withCredentials: true }),
          axios.get(`${API_URL}/api/reports/expenses-by-category?month=${selectedMonth}&year=${selectedYear}${currencyParam}`, { withCredentials: true }),
          axios.get(`${API_URL}/api/reports/employee-earnings?month=${selectedMonth}&year=${selectedYear}`, { withCredentials: true })
        ]);
        setIncomeByProject(incomeRes.data);
        setExpensesByType(expensesRes.data);
        setExpensesByCategory(categoryRes.data || { by_category: [], by_project: [] });
        setEmployeeEarnings(earningsRes.data);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/export/${format}?month=${selectedMonth}&year=${selectedYear}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${selectedMonth}_${selectedYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('toast.exported', { format: format.toUpperCase() }));
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error(t('toast.exportFailed'));
    }
  };

  const INCOME_COLORS = ['#047857', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'];
  const EXPENSE_COLORS = ['#ef4444', '#f97316', '#eab308', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1', '#84cc16', '#0ea5e9', '#a855f7', '#10b981', '#f43f5e', '#64748b'];
  const COLORS = INCOME_COLORS;

  // Known category codes get a translated label; anything else shows as stored.
  const categoryLabel = (code) => t(`categories.${code}`, { defaultValue: code });
  const months = t('common:months', { returnObjects: true });

  // --- Invoice / Telegram ---
  const openInvoiceDialog = async () => {
    setInvoiceDialogOpen(true);
    setInvoiceResults(null);
    setSelectedInvoiceEmpIds([]);
    setLoadingInvoiceEmps(true);
    try {
      const res = await axios.get(`${API_URL}/api/invoices/employees?month=${selectedMonth}&year=${selectedYear}`, { withCredentials: true });
      setInvoiceEmployees(res.data || []);
      // Pre-select employees that have telegram + earnings
      const preSelected = (res.data || []).filter(e => e.has_telegram && e.has_earnings).map(e => e.employee_id);
      setSelectedInvoiceEmpIds(preSelected);
    } catch (err) {
      console.error('Error loading invoice employees:', err);
      toast.error(t('toast.invoiceEmployeesFailed'));
    } finally {
      setLoadingInvoiceEmps(false);
    }
  };

  const toggleInvoiceEmployee = (empId) => {
    setSelectedInvoiceEmpIds(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  const handleSendInvoices = async () => {
    if (selectedInvoiceEmpIds.length === 0) {
      toast.error(t('toast.noEmployeeSelected'));
      return;
    }
    setSendingInvoices(true);
    setInvoiceResults(null);
    try {
      const res = await axios.post(`${API_URL}/api/invoices/send`, {
        month: selectedMonth,
        year: selectedYear,
        employee_ids: selectedInvoiceEmpIds,
        show_income: showIncome,
      }, { withCredentials: true });
      setInvoiceResults(res.data);
      if (res.data.success_count > 0) {
        toast.success(t('toast.invoicesSent', { count: res.data.success_count }));
        const sentIds = new Set((res.data.results || []).filter(r => r.status === 'sent').map(r => r.employee_id));
        setInvoiceEmployees(prev => prev.map(e => sentIds.has(e.employee_id) ? { ...e, is_paid: true } : e));
      }
      if (res.data.fail_count > 0) {
        toast.error(t('toast.invoicesFailed', { count: res.data.fail_count }));
      }
    } catch (err) {
      console.error('Error sending invoices:', err);
      const msg = err.response?.data?.detail || t('toast.sendFailed');
      toast.error(msg);
    } finally {
      setSendingInvoices(false);
    }
  };

  const handleToggleInvoicePaid = async (emp) => {
    const newPaid = !emp.is_paid;
    setInvoiceEmployees(prev => prev.map(e => e.employee_id === emp.employee_id ? { ...e, is_paid: newPaid } : e));
    try {
      await axios.post(`${API_URL}/api/invoices/mark-paid`, {
        employee_id: emp.employee_id,
        year: selectedYear,
        month: selectedMonth,
        is_paid: newPaid,
      }, { withCredentials: true });
      toast.success(newPaid ? t('toast.markedPaid', { name: emp.full_name }) : t('toast.unmarkedPaid', { name: emp.full_name }));
    } catch {
      setInvoiceEmployees(prev => prev.map(e => e.employee_id === emp.employee_id ? { ...e, is_paid: !newPaid } : e));
      toast.error(t('toast.markFailed'));
    }
  };

  const handleDownloadInvoicePdf = async (empId, empName) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/pdf/${empId}?month=${selectedMonth}&year=${selectedYear}&show_income=${showIncome}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${empName}_${selectedMonth}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading invoice PDF:', err);
      toast.error(t('toast.pdfFailed'));
    }
  };

  // Format currency based on type
  const formatCurrency = (amount, currency = 'USD') => {
    if (currency === 'UZS') {
      return `${Math.round(amount).toLocaleString()} UZS`;
    }
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Display amount in selected currency
  const fmtUsd = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtUzs = (amount) => `${Math.round(amount || 0).toLocaleString()} UZS`;
  const displayAmount = useCallback((usdAmount, uzsAmount) => {
    if (displayCurrency === 'UZS') {
      if (uzsAmount !== undefined && uzsAmount !== null) return fmtUzs(uzsAmount);
      if (exchangeRate) return fmtUzs(usdAmount * exchangeRate);
    }
    return fmtUsd(usdAmount);
  }, [displayCurrency, exchangeRate]);

  // Compute exact UZS for an employee — uses earning_uzs per project when available
  const getEarningUzs = useCallback((earning) => {
    if (displayCurrency !== 'UZS') return null;
    // Use total_earnings_uzs from backend if available
    if (earning.total_earnings_uzs != null) return earning.total_earnings_uzs;
    const projects = earning.projects || [];
    if (!projects.some(p => p.earning_uzs != null)) return null;
    return projects.reduce((sum, p) =>
      sum + (p.earning_uzs != null ? p.earning_uzs : Math.round(p.earning * (exchangeRate || 1))), 0
    );
  }, [displayCurrency, exchangeRate]);

  // Format amount with currency breakdown
  const formatAmountWithCurrencies = (item) => {
    if (!item.currencies || item.currencies.length === 0) {
      return formatCurrency(item.total_income || item.total_amount || 0);
    }
    
    // If only one currency, show in that currency
    if (item.currencies.length === 1) {
      return formatCurrency(item.currencies[0].amount, item.currencies[0].currency);
    }
    
    // Multiple currencies - show breakdown
    return item.currencies.map(c => formatCurrency(c.amount, c.currency)).join(' + ');
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-slate-900 dark:text-slate-100">{data.project_name || (data.category ? categoryLabel(data.category) : data.expense_type)}</p>
          {data.currencies && data.currencies.map((c, idx) => (
            <p key={idx} className="text-sm text-slate-600 dark:text-slate-400">
              {formatCurrency(c.amount, c.currency)}
            </p>
          ))}
          {data.total_income_usd && (
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-1 border-t pt-1">
              {t('charts.converted', { amount: displayAmount(data.total_income_usd, data.total_income_uzs) })}
            </p>
          )}
          {data.total_amount_usd && (
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-1 border-t pt-1">
              {t('charts.converted', { amount: displayAmount(data.total_amount_usd) })}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{t('title')}</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {isEmployee ? t('subtitleEmployee') : t('subtitle')}
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1">
        <div className="flex flex-wrap gap-2 items-end">
          {!isEmployee && (
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">{t('filters.data')}</Label>
              <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                <SelectTrigger className="w-[140px]" data-testid="report-currency-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="combined">{t('filters.allCurrencies')}</SelectItem>
                  <SelectItem value="USD">{t('filters.usdOnly')}</SelectItem>
                  <SelectItem value="UZS">{t('filters.uzsOnly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('filters.display')}</Label>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden h-10">
              <button
                className={`px-3 text-sm font-medium transition-colors ${displayCurrency === 'USD' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                onClick={() => setDisplayCurrency('USD')}
              >$ USD</button>
              <button
                className={`px-3 text-sm font-medium transition-colors ${displayCurrency === 'UZS' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                onClick={() => setDisplayCurrency('UZS')}
              >UZS</button>
            </div>
          </div>
          <Select value={selectedMonth.toString()} onValueChange={(val) => setSelectedMonth(parseInt(val))}>
            <SelectTrigger className="w-[140px]" data-testid="report-month-selector"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((month, idx) => (<SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>))}</SelectContent>
          </Select>
          <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
            <SelectTrigger className="w-[120px]" data-testid="report-year-selector"><SelectValue /></SelectTrigger>
            <SelectContent>{[2024, 2025, 2026].map((year) => (<SelectItem key={year} value={year.toString()}>{year}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <MonthRate month={selectedMonth} year={selectedYear} onRate={setExchangeRate} />
        </div>
      </div>

      {!isEmployee && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => handleExport('excel')} data-testid="export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-2" />{t('actions.exportExcel')}</Button>
          <Button variant="outline" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-2" />{t('actions.exportPdf')}</Button>
          <Button variant="outline" onClick={openInvoiceDialog} className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40" data-testid="send-invoices-btn">
            <Send className="h-4 w-4 mr-2" />
            {t('actions.sendInvoices')}
          </Button>
        </div>
      )}

      {/* Invoice Send Dialog */}
      {!isEmployee && (
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <Send className="h-5 w-5 inline mr-2 text-blue-600" />
              {t('invoice.dialogTitle', { period: `${months[selectedMonth - 1]} ${selectedYear}` })}
            </DialogTitle>
          </DialogHeader>

          {loadingInvoiceEmps ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-slate-500">{t('invoice.loadingEmployees')}</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Show income toggle */}
              <div
                onClick={() => setShowIncome(!showIncome)}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer select-none transition-all ${
                  showIncome
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-400 dark:border-emerald-600'
                    : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                }`}
              >
                <div className={`flex items-center justify-center w-10 h-6 rounded-full transition-colors ${showIncome ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${showIncome ? 'translate-x-2' : '-translate-x-2'}`} />
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${showIncome ? 'text-emerald-800 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-400'}`}>
                    {showIncome ? t('invoice.incomeShown') : t('invoice.incomeHidden')}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {showIncome
                      ? t('invoice.incomeShownHint')
                      : t('invoice.incomeHiddenHint')}
                  </div>
                </div>
              </div>
              {!showIncome && (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium -mt-2 ml-1">
                  {t('invoice.fixedAutoHidden')}
                </p>
              )}
              {/* Employee list with checkboxes */}
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900">
                    <tr>
                      <th className="px-4 py-2 text-left w-10">
                        <Checkbox
                          checked={selectedInvoiceEmpIds.length === invoiceEmployees.filter(e => e.has_telegram && e.has_earnings).length && selectedInvoiceEmpIds.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedInvoiceEmpIds(invoiceEmployees.filter(e => e.has_telegram && e.has_earnings).map(e => e.employee_id));
                            } else {
                              setSelectedInvoiceEmpIds([]);
                            }
                          }}
                        />
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">{t('invoice.headers.employee')}</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">{t('invoice.headers.telegram')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">{t('invoice.headers.netSalary')}</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500">{t('invoice.headers.paid')}</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500">{t('invoice.headers.pdf')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceEmployees.map((emp) => {
                      const result = invoiceResults?.results?.find(r => r.employee_id === emp.employee_id);
                      return (
                        <tr key={emp.employee_id} className={`border-b border-slate-100 dark:border-slate-800 transition-colors ${emp.is_paid ? 'bg-emerald-50 dark:bg-emerald-900/10' : ''} ${!emp.has_earnings ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-2">
                            <Checkbox
                              checked={selectedInvoiceEmpIds.includes(emp.employee_id)}
                              disabled={!emp.has_telegram || !emp.has_earnings}
                              onCheckedChange={() => toggleInvoiceEmployee(emp.employee_id)}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <div className="font-medium text-slate-800 dark:text-slate-200">{emp.full_name}</div>
                            <div className="text-xs text-slate-500">
                              {emp.position}
                              {emp.is_fixed_salary && (
                                <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">{t('invoice.fixedSalaryTag')}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            {emp.has_telegram ? (
                              <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                                <CheckCircle className="h-3 w-3" /> {t('invoice.connected')}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                                <XCircle className="h-3 w-3" /> {t('invoice.notSet')}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right font-medium text-emerald-700 dark:text-emerald-400">
                            {emp.has_earnings ? `$${emp.net_salary.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {emp.has_earnings && (
                              <button
                                onClick={() => handleToggleInvoicePaid(emp)}
                                title={emp.is_paid ? t('invoice.paidUnmarkTitle') : t('invoice.paidMarkTitle')}
                                className={`w-7 h-7 rounded-md border-2 flex items-center justify-center mx-auto transition-colors ${
                                  emp.is_paid
                                    ? 'bg-emerald-500 border-emerald-500 text-white'
                                    : 'border-slate-300 dark:border-slate-600 text-transparent hover:border-emerald-400'
                                }`}
                              >
                                <CheckCircle className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {emp.has_earnings && (
                              <Button size="sm" variant="ghost" onClick={() => handleDownloadInvoicePdf(emp.employee_id, emp.full_name)} title={t('invoice.downloadPdf')}>
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Send results */}
              {invoiceResults && (
                <div className="rounded-lg border p-3 space-y-2">
                  <div className="flex gap-4 text-sm">
                    <span className="text-emerald-600 font-medium">{t('invoice.sentCount', { count: invoiceResults.success_count })}</span>
                    <span className="text-red-600 font-medium">{t('invoice.failedCount', { count: invoiceResults.fail_count })}</span>
                  </div>
                  {invoiceResults.results?.filter(r => r.status === 'failed').map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-red-500">
                      <AlertTriangle className="h-3 w-3" />
                      {r.employee_name}: {r.reason}
                    </div>
                  ))}
                </div>
              )}

              {/* Paid summary */}
              {invoiceEmployees.filter(e => e.has_earnings).length > 0 && (
                <div className="text-xs text-slate-500 text-right">
                  <Trans
                    t={t}
                    i18nKey="invoice.paidSummary"
                    values={{ paid: invoiceEmployees.filter(e => e.is_paid).length, total: invoiceEmployees.filter(e => e.has_earnings).length }}
                    components={{ 1: <span className="font-semibold text-emerald-600" /> }}
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex justify-between items-center pt-2">
                <span className="text-sm text-slate-500">
                  {t('invoice.selectedCount', { count: selectedInvoiceEmpIds.length })}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>{t('common:actions.cancel')}</Button>
                  <Button
                    onClick={handleSendInvoices}
                    disabled={sendingInvoices || selectedInvoiceEmpIds.length === 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {sendingInvoices ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('invoice.sending')}</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> {t('invoice.sendViaTelegram')}</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Employee Monthly Trend */}
      {isEmployee && myTrend.length > 0 && (() => {
        const shortMonths = t('common:monthsShort', { returnObjects: true });
        const chartData = myTrend.map(row => ({
          name: shortMonths[row.month - 1],
          month: row.month,
          earnings: displayCurrency === 'UZS' ? row.earnings_uzs : row.earnings_usd,
        }));
        const hasData = chartData.some(d => d.earnings !== 0);
        if (!hasData) return null;
        return (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('trend.title', { year: selectedYear })}</CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">{t('trend.subtitle')}</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" fontSize={11} stroke="#94a3b8" />
                  <YAxis fontSize={11} stroke="#94a3b8" tickFormatter={(v) => displayCurrency === 'UZS' ? `${(v / 1000000).toFixed(1)}M` : `$${v.toLocaleString()}`} />
                  <Tooltip
                    formatter={(value) => [displayCurrency === 'UZS' ? `${Math.round(value).toLocaleString()} UZS` : `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, t('trend.tooltipLabel')]}
                    labelFormatter={(label) => label}
                  />
                  <Bar dataKey="earnings" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.month === selectedMonth ? '#047857' : '#a7f3d0'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      })()}

      {/* Employee Personal Stats */}
      {isEmployee && mySalary && (() => {
        const myData = mySalary;
        const totalUzs = myData.total_earnings_uzs || 0;
        const totalUsd = myData.total_earnings || 0;
        const projectCount = myData.projects?.length || 0;
        const maxEarningUzs = Math.max(...(myData.projects || []).map(p => p.earning_uzs || 0), 1);
        const globalExpUzs = myData.global_expense_share_uzs || 0;

        const PROJECT_COLORS = ['#047857', '#10B981', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ec4899', '#14b8a6', '#6366f1'];

        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Total Earnings Card */}
            <Card className="sm:col-span-1">
              <CardContent className="pt-6">
                <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('mySalary.total')}</div>
                <div className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">
                  {displayCurrency === 'UZS' ? fmtUzs(totalUzs) : fmtUsd(totalUsd)}
                </div>
                {globalExpUzs > 0 && (
                  <div className="text-xs text-red-500 mt-2">
                    {t('mySalary.globalExpense', { amount: displayCurrency === 'UZS' ? fmtUzs(globalExpUzs) : fmtUsd(myData.global_expense_share || 0) })}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20">
                    <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">{t('mySalary.projectCount', { count: projectCount })}</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700">
                    <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{months[selectedMonth - 1]} {selectedYear}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Per-Project Progress Bars */}
            <Card className="sm:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('mySalary.byProjectsTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                {myData.projects && myData.projects.length > 0 ? (
                  <div className="space-y-3">
                    {[...myData.projects]
                      .sort((a, b) => (b.earning_uzs || 0) - (a.earning_uzs || 0))
                      .map((proj, idx) => {
                        const earningUzs = proj.earning_uzs || 0;
                        const earningUsd = proj.earning_usd || 0;
                        const pct = maxEarningUzs > 0 ? (earningUzs / maxEarningUzs) * 100 : 0;
                        const color = PROJECT_COLORS[idx % PROJECT_COLORS.length];
                        return (
                          <div key={proj.project_id}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate mr-2">
                                {proj.project_name}
                              </span>
                              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                                {displayCurrency === 'UZS' ? fmtUzs(earningUzs) : fmtUsd(earningUsd)}
                              </span>
                            </div>
                            <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2.5">
                              <div
                                className="h-2.5 rounded-full transition-all duration-500"
                                style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color }}
                              />
                            </div>
                            <div className="flex justify-between mt-0.5">
                              <span className="text-[11px] text-slate-400">
                                {proj.payment_type === 'percentage' ? t('mySalary.shareLabel', { pct: proj.percentage }) : t('mySalary.fixedLabel', { amount: formatCurrency(proj.fixed_amount || 0, proj.fixed_currency || 'USD') })}
                              </span>
                              <span className="text-[11px] text-slate-400">
                                {totalUzs > 0 ? t('mySalary.ofTotal', { pct: Math.round((earningUzs / (totalUzs + globalExpUzs)) * 100) }) : ''}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : myData.earning_type === 'global_fixed' ? (
                  <p className="text-sm text-blue-600 dark:text-blue-400">{t('mySalary.fixedAssigned')}</p>
                ) : (
                  <p className="text-sm text-slate-500">{t('mySalary.noDataMonth')}</p>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {!isEmployee && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>{t('charts.incomeByProject')}</CardTitle></CardHeader>
          <CardContent>
            {incomeByProject.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie 
                      data={incomeByProject} 
                      dataKey="total_income_usd" 
                      nameKey="project_name" 
                      cx="50%" 
                      cy="50%" 
                      outerRadius={80}
                    >
                      {incomeByProject.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                {/* Income breakdown table */}
                <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
                        <th className="pb-2">{t('common:fields.project')}</th>
                        <th className="pb-2 text-right">{t('common:fields.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incomeByProject.map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-2 text-slate-700 dark:text-slate-300">{item.project_name}</td>
                          <td className="py-2 text-right font-medium text-emerald-700 dark:text-emerald-400">
                            {formatAmountWithCurrencies(item)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (<p className="text-center text-slate-500 py-12">{t('charts.noData')}</p>)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('charts.expensesByCategory')}</CardTitle></CardHeader>
          <CardContent>
            {expensesByCategory.by_category.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={expensesByCategory.by_category}
                      dataKey="total_usd"
                      nameKey="category"
                      cx="50%" cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                    >
                      {expensesByCategory.by_category.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [displayAmount(value), categoryLabel(props.payload.category)]} />
                    <Legend formatter={(value) => categoryLabel(value)} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Category breakdown table */}
                <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-4 space-y-1 max-h-48 overflow-y-auto">
                  {expensesByCategory.by_category.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: EXPENSE_COLORS[idx % EXPENSE_COLORS.length] }} />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{categoryLabel(item.category)}</span>
                        <span className="text-xs text-slate-400">({item.item_count})</span>
                      </div>
                      <span className="text-sm font-medium text-red-600 dark:text-red-400">{displayAmount(item.total_usd)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (<p className="text-center text-slate-500 py-12">{t('charts.noData')}</p>)}
          </CardContent>
        </Card>
      </div>

      {/* Expenses by Project bar chart */}
      {expensesByCategory.by_project.length > 0 && (
        <Card>
          <CardHeader><CardTitle>{t('charts.projectExpenses')}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(200, expensesByCategory.by_project.length * 45)}>
              <BarChart data={expensesByCategory.by_project} layout="vertical" margin={{ left: 20, right: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" stroke="#64748b" fontSize={11}
                  tickFormatter={(val) => displayCurrency === 'UZS' && exchangeRate
                    ? `${(val * exchangeRate / 1000000).toFixed(1)}M`
                    : `$${val.toLocaleString()}`}
                />
                <YAxis type="category" dataKey="project_name" stroke="#64748b" fontSize={12} width={110} />
                <Tooltip formatter={(value) => [displayAmount(value), t('charts.expensesSeries')]} />
                <Bar dataKey="total_usd" fill="#ef4444" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="total_usd" position="right" fontSize={11}
                    formatter={(value) => displayCurrency === 'UZS' && exchangeRate
                      ? `${Math.round(value * exchangeRate / 1000000).toLocaleString()}M`
                      : `$${value.toLocaleString()}`}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      </>
      )}

      {/* DETAILED EMPLOYEE EARNINGS BREAKDOWN */}
      <Card>
        <CardHeader>
          <CardTitle>{isEmployee ? t('earnings.titleEmployee') : t('earnings.title')}</CardTitle>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {isEmployee ? t('earnings.subtitleEmployee') : t('earnings.subtitle')}
          </p>
        </CardHeader>
        <CardContent>
          {(() => {
            const earningsList = isEmployee && mySalary ? [mySalary] : employeeEarnings;
            return earningsList.length > 0 ? (
            <div className="space-y-6">
              {earningsList.map((earning, idx) => (
                <div key={earning.employee_id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-6 bg-slate-50 dark:bg-slate-800/50">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{earning.employee_name}</h3>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        earning.earning_type === 'global_fixed'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                          : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                      }`}>
                        {earning.earning_type === 'global_fixed' ? t('earnings.fixedSalary') : t('earnings.projectBased')}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-slate-600 dark:text-slate-400">{t('earnings.totalEarnings')}</div>
                      <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                        {(() => { const uzs = getEarningUzs(earning); return uzs != null ? fmtUzs(uzs) : displayAmount(earning.total_earnings); })()}
                      </div>
                      {earning.global_expense_share > 0 && (
                        <div className="text-xs text-red-500 dark:text-red-400 mt-1">
                          {t('earnings.globalExpenseShare', { amount: displayAmount(earning.global_expense_share, earning.global_expense_share_uzs) })}
                        </div>
                      )}
                      {earning.earning_type === 'global_fixed' && earning.salary != null && (
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {t('earnings.fixedAmount', { amount: formatCurrency(earning.salary, earning.salary_currency || 'USD') })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Per-Project Breakdown */}
                  {earning.projects && earning.projects.length > 0 ? (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('earnings.projectBreakdown')}</div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 dark:bg-slate-700">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('earnings.headers.project')}</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('earnings.headers.status')}</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('earnings.headers.type')}</th>
                              {!isEmployee && (
                                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('earnings.headers.projectIncome')}</th>
                              )}
                              <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('earnings.headers.earnings')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {earning.projects.map((project, pidx) => (
                              <tr key={pidx} className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                                <td className="px-4 py-2">
                                  <Link 
                                    to="/projects" 
                                    className="text-emerald-700 dark:text-emerald-400 hover:underline font-medium inline-flex items-center gap-1"
                                  >
                                    {project.project_name}
                                    <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </td>
                                <td className="px-4 py-2">
                                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                    project.project_status === 'active' 
                                      ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                                      : project.project_status === 'paused'
                                      ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                  }`}>
                                    {t(`earnings.projectStatus.${project.project_status}`, { defaultValue: project.project_status })}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-slate-700 dark:text-slate-300">
                                  {project.payment_type === 'percentage'
                                    ? `${project.percentage}%`
                                    : t('earnings.fixedType', { amount: formatCurrency(project.fixed_amount || 0, project.fixed_currency || 'USD') })}
                                </td>
                                {!isEmployee && (
                                <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">
                                  {(displayCurrency === 'UZS' && project.project_income_uzs != null)
                                    ? `${Math.round(project.project_income_uzs).toLocaleString()} UZS`
                                    : displayAmount(project.project_income || 0)}
                                </td>
                                )}
                                <td className="px-4 py-2 text-right font-medium text-emerald-700 dark:text-emerald-400">
                                  {(displayCurrency === 'UZS' && project.earning_uzs != null)
                                    ? `${Math.round(project.earning_uzs).toLocaleString()} UZS`
                                    : displayAmount(project.earning_usd ?? project.earning)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : earning.earning_type === 'global_fixed' ? (
                    <p className="text-sm text-blue-600 dark:text-blue-400">{t('earnings.fixedFromBudget')}</p>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('earnings.noAssignments')}</p>
                  )}
                </div>
              ))}

              {/* Summary Section */}
              {!isEmployee && (
              <div className="border-t-2 border-emerald-600 dark:border-emerald-500 pt-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
                    <div className="text-sm text-emerald-700 dark:text-emerald-400 mb-1">{t('earnings.summary.totalPaid')}</div>
                    <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                      {(() => {
                        if (displayCurrency === 'UZS' && exchangeRate) {
                          const totalUzs = employeeEarnings.reduce((sum, e) => {
                            const uzs = getEarningUzs(e);
                            return sum + (uzs != null ? uzs : Math.round(e.total_earnings * exchangeRate));
                          }, 0);
                          return fmtUzs(totalUzs);
                        }
                        return fmtUsd(employeeEarnings.reduce((sum, e) => sum + e.total_earnings, 0));
                      })()}
                    </div>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <div className="text-sm text-blue-700 dark:text-blue-400 mb-1">{t('earnings.summary.projectBased')}</div>
                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                      {employeeEarnings.filter(e => e.earning_type !== 'global_fixed').length}
                    </div>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                    <div className="text-sm text-orange-700 dark:text-orange-400 mb-1">{t('earnings.summary.fixedSalary')}</div>
                    <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                      {employeeEarnings.filter(e => e.earning_type === 'global_fixed').length}
                    </div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <div className="text-sm text-purple-700 dark:text-purple-400 mb-1">{t('earnings.summary.period')}</div>
                    <div className="text-lg font-semibold text-purple-700 dark:text-purple-400">{months[selectedMonth - 1]} {selectedYear}</div>
                  </div>
                </div>
              </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-500 dark:text-slate-400">{t('earnings.empty')}</p>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">{t('earnings.emptyHint')}</p>
            </div>
          );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}

export default Reports;
